import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { SignInButton, SignUpButton, UserButton, useUser } from '@clerk/clerk-react';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { isSignedIn, user } = useUser();

  const navigation = {
    main: [
      { name: 'Home', href: '/', current: location.pathname === '/' },
      { name: 'Players', href: '/players', current: location.pathname === '/players' },
      { name: 'Trade Calculator', href: '/trade-calculator', current: location.pathname === '/trade-calculator' },
      { name: 'Draft Assistant', href: '/draft-assistant', current: location.pathname === '/draft-assistant' },
      { name: 'My Roster', href: '/my-roster', current: location.pathname === '/my-roster' },
    ]
  };

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  return (
    <>
      <nav
        className={`sticky top-0 w-full z-50 transition-all duration-300 ${
          scrolled ? 'bg-black/95 backdrop-blur-md shadow-lg border-b border-gray-800' : 'bg-black'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-28">
            <div className="flex items-center">
              <Link to="/" className="flex-shrink-0 group">
                <motion.img
                  src="/images/Fantasy Fiesta Logo - Transparent.png"
                  alt="Fantasy Fiesta"
                  className="h-20 w-auto object-contain transition-all duration-300"
                  whileHover={{ 
                    scale: 1.1, 
                    rotate: 360,
                    transition: { duration: 0.6, ease: "easeInOut" }
                  }}
                  whileTap={{ scale: 0.95 }}
                />
              </Link>
              <div className="hidden md:block ml-12">
                <div className="flex items-center space-x-1">
                  {navigation.main.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`
                        px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 relative group
                        ${
                          item.current
                            ? 'text-fiesta-yellow bg-fiesta-yellow/10'
                            : 'text-gray-300 hover:text-white hover:bg-gray-800'
                        }
                      `}
                    >
                      {item.name}
                      {!item.current && (
                        <span className="absolute inset-x-0 -bottom-1 h-0.5 bg-fiesta-blue scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left"></span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Auth Section */}
            <div className="hidden md:flex items-center space-x-4">
              {isSignedIn ? (
                <div className="flex items-center space-x-3">
                  <motion.span 
                    className="text-gray-400 text-sm"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    Hey, {user?.firstName}! 👋
                  </motion.span>
                  <UserButton 
                    appearance={{
                      elements: {
                        avatarBox: "w-9 h-9 border-2 border-fiesta-blue/30 hover:border-fiesta-blue/60 transition-colors",
                        userButtonPopoverCard: "bg-gray-900 border border-gray-700 shadow-2xl",
                        userButtonPopoverActionButton: "text-white hover:bg-gray-700 transition-colors",
                        userButtonPopoverActionButtonText: "text-white font-medium",
                        userButtonPopoverFooter: "bg-gray-800 border-t border-gray-600",
                        userButtonPopoverActionButtonIcon: "text-gray-300",
                      },
                      variables: {
                        colorBackground: "#111827",
                        colorText: "#ffffff",
                        colorTextSecondary: "#d1d5db",
                        colorNeutral: "#374151",
                        colorDanger: "#ef4444",
                        colorSuccess: "#10b981",
                        colorWarning: "#f59e0b",
                        colorTextOnPrimaryBackground: "#ffffff"
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <SignInButton mode="modal">
                    <motion.button 
                      className="px-4 py-2 text-gray-300 hover:text-white font-medium transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Sign In
                    </motion.button>
                  </SignInButton>
                  <SignUpButton mode="modal">
                    <motion.button 
                      className="px-6 py-2 bg-fiesta-blue hover:bg-fiesta-blue/80 text-white font-medium rounded-lg transition-colors relative overflow-hidden group"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span className="relative z-10">Get Started 🚀</span>
                      <div className="absolute inset-0 bg-gradient-to-r from-fiesta-blue to-fiesta-pink opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </motion.button>
                  </SignUpButton>
                </div>
              )}
            </div>
            
            {/* Mobile hamburger */}
            <div className="flex md:hidden items-center space-x-3">
              {isSignedIn && (
                <UserButton 
                  appearance={{
                    elements: {
                      avatarBox: "w-8 h-8 border border-fiesta-blue/30",
                      userButtonPopoverCard: "bg-gray-900 border border-gray-700 shadow-2xl",
                      userButtonPopoverActionButton: "text-white hover:bg-gray-700 transition-colors",
                      userButtonPopoverActionButtonText: "text-white font-medium",
                      userButtonPopoverFooter: "bg-gray-800 border-t border-gray-600",
                      userButtonPopoverActionButtonIcon: "text-gray-300",
                    },
                    variables: {
                      colorBackground: "#111827",
                      colorText: "#ffffff",
                      colorTextSecondary: "#d1d5db",
                      colorNeutral: "#374151",
                      colorDanger: "#ef4444",
                      colorSuccess: "#10b981",
                      colorWarning: "#f59e0b",
                      colorTextOnPrimaryBackground: "#ffffff"
                    }
                  }}
                />
              )}
              
              <motion.button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
                whileTap={{ scale: 0.95 }}
              >
                <span className="sr-only">Open menu</span>
                <motion.div
                  animate={{ rotate: isOpen ? 90 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {isOpen ? (
                    <XMarkIcon className="block h-6 w-6" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" />
                  )}
                </motion.div>
              </motion.button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-black/95 backdrop-blur-sm sticky top-28 z-40 border-b border-gray-800"
          >
            <div className="px-4 py-4 space-y-2">
              {navigation.main.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.05 }}
                >
                  <Link
                    to={item.href}
                    className={`
                      block px-4 py-3 rounded-lg font-medium transition-colors duration-200
                      ${
                        item.current
                          ? 'text-fiesta-yellow bg-fiesta-yellow/10'
                          : 'text-gray-300 hover:text-white hover:bg-gray-800'
                      }
                    `}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                </motion.div>
              ))}
              
              {!isSignedIn && (
                <motion.div 
                  className="pt-4 border-t border-gray-800 space-y-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2, delay: 0.3 }}
                >
                  <SignInButton mode="modal">
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg font-medium transition-colors"
                      onClick={() => setIsOpen(false)}
                    >
                      Sign In
                    </button>
                  </SignInButton>
                  <SignUpButton mode="modal">
                    <button 
                      className="w-full text-left px-4 py-3 bg-fiesta-blue hover:bg-fiesta-blue/80 text-white rounded-lg font-medium transition-colors"
                      onClick={() => setIsOpen(false)}
                    >
                      Get Started 🚀
                    </button>
                  </SignUpButton>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};